import React, { useState } from "react";

import { API } from "aws-amplify";

import { makeStyles } from "@material-ui/core/styles";
import { alpha } from "@material-ui/core/styles/colorManipulator";
import classnames from "classnames";

import { Confirm, SaveButton, useNotify } from "react-admin";
import { useForm } from "react-final-form";
import { useHistory } from "react-router-dom";

import { sanitizeInbound } from "../../../../graphql/inbound/inboundCRUD";
import { logError } from "../../../../utils/appUtils";
import {
  FilePaths,
  InboundStatus,
  NoteType,
} from "../../../../utils/constants";
import { saveRemoveFile, saveRemoveFiles } from "../../../../utils/fileUtils";
import { deepRedirect } from "../../../../utils/utils";
import { RejectIcon } from "../../../../components/common/iconography";
import { emailInboundAdviceRejectedHTML } from "../emailInboundAdviceHTML";

const useStyles = makeStyles((theme) => ({
  rejectButton: {
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: alpha(theme.palette.error.main, 0.12),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
}));

// Manager-side reject for an inbound advice under review (ADVICE_REVIEW /
// ADVICE_FAILED). Mirrors the reject half of the outbound
// AdviceApproveRejectButton: requires a documented reason note, flips the
// status to ADVICE_REJECTED, and emails the submitter (advisedBy). Persists via
// the plain AppSync update path (no __saveContext) — advice records are not
// lambda-backed and a reject only touches the parent shipment.
// Only rendered for externally-submitted advices (see isExternalAdvice in
// InboundUtils); a yard-created advice has no submitter to return it to.
export const AdviceRejectButton = ({
  record,
  backPath,
  isSaving,
  setIsSaving,
  isDeleting,
  loading,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const history = useHistory();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");

  const handleValidateReject = () => {
    const formData = form.getState().values;
    // Require the rejection reason to be documented as a fresh (undated) note.
    const notesMaxIndex = formData.notes ? formData.notes.length - 1 : -1;
    const lastNote =
      notesMaxIndex >= 0 ? formData.notes[notesMaxIndex] : undefined;

    if (!lastNote || lastNote.date) {
      setMessage(
        "Don't forget to document the reason for rejection in the Notes! Proceed with rejection?",
      );
      setOpen(true);
    } else {
      handleSave();
    }
  };

  const handleDialogClose = () => setOpen(false);
  const handleDialogConfirm = () => handleSave();

  const handleSave = () => {
    setOpen(false);
    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    setIsSaving(true);
    data = { ...data, status: InboundStatus.ADVICE_REJECTED };

    data.history = [
      ...(data.history || []),
      {
        text: "Advice rejected.",
        by: window.appUser?.fullName,
        date: new Date(),
        type: NoteType.SHIPMENT,
      },
    ];

    if (data.notes?.length > 0) {
      data.notes.forEach((element) => {
        if (!element.date) {
          element.type = NoteType.GENERIC;
          element.date = new Date();
          element.by = window.appUser?.fullName;
        }
      });
    } else data.notes = undefined;

    // Upload/prune files before persisting — a file the manager dropped while
    // reviewing is still a raw dropzone entry ({rawFile, src, title}) that
    // S3ObjectInput rejects, and files removed from the form must be deleted
    // from S3. Mirrors the parent DialogSaveButton's transform.
    try {
      await Promise.all([
        saveRemoveFiles(
          record,
          data,
          "attachments",
          `${FilePaths.INBOUND}/${Date.now()}`,
        ),
        saveRemoveFile(
          record,
          data,
          "receiptFile",
          `${FilePaths.INBOUND}/${Date.now()}`,
        ),
      ]);
    } catch (err) {
      logError("inboundShipments:AdviceRejectButton:transform", err, notify);
      setIsSaving(false);
      return Promise.reject();
    }

    // Plain AppSync update: no __saveContext, so the dataProvider persists via
    // updateInboundShipment rather than the lambda writer.
    delete data.customFieldSelect;
    return sanitizeInbound(data);
  };

  const onSuccess = ({ data }) => {
    try {
      const emailContent = emailInboundAdviceRejectedHTML(
        window.appUser.firstName,
        window.appUser.email,
        window.appUser.reloadYard,
        data,
      );
      if (data.advisedBy) {
        API.post("transREST", "/sendEmail", {
          body: {
            to: data.advisedBy,
            subject: "Inbound Advice Rejected",
            template: "emailTemplate.html",
            htmlContent: emailContent,
          },
        });
      } else {
        // Unreachable via the UI (the button is origin-gated), so a hit here
        // means the gate regressed and a reject went out unannounced.
        logError(
          "inboundShipments:AdviceRejectButton:noRecipient",
          `advice ${data.id} rejected with no advisedBy`,
          false,
        );
      }
    } catch (error) {
      logError("inboundShipments:AdviceRejectButton:email", error, false);
    }

    notify("Advice rejected", "success");
    // backPath is the breadcrumb array (or a plain path) — deepRedirect
    // resolves both; useRedirect would join an array into one bogus path.
    deepRedirect(history, backPath);
    setIsSaving(false);
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("inboundShipments:AdviceRejectButton:onFailure", error, notify);
  };

  return (
    <>
      <SaveButton
        {...props}
        label="Reject"
        variant="outlined"
        color="default"
        className={classnames("ra-save-button", classes.rejectButton)}
        submitOnEnter={false}
        undoable={false}
        disabled={isSaving || isDeleting || loading}
        saving={isSaving}
        handleSubmitWithRedirect={handleValidateReject}
        transform={transform}
        onSuccess={onSuccess}
        onFailure={onFailure}
        icon={<RejectIcon />}
      />
      <Confirm
        isOpen={open}
        loading={isSaving}
        title="Reject Confirmation"
        content={message}
        onConfirm={handleDialogConfirm}
        onClose={handleDialogClose}
      />
    </>
  );
};
